import { useEffect, useRef, useState } from "react";
import jspreadsheet from "jspreadsheet-ce";
import tabularjs from "tabularjs";
import "jspreadsheet-ce/dist/jspreadsheet.css";
import { X } from "lucide-react";
import Spinner from "../../Shared/Spinner";

interface FilePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileUrl: string;
  fileType: string;
}

const FilePreviewModal = ({
  isOpen,
  onClose,
  fileUrl,
  fileType,
}: FilePreviewModalProps) => {
  const spreadsheetRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const jssInstance = useRef<any>(null);

  useEffect(() => {
    if (!isOpen || !fileUrl) return;

    const loadFile = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch the file from the URL
        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error("Failed to fetch file");
        }

        const blob = await response.blob();

        if (fileType === "spreadsheet") {
          // Parse spreadsheet with tabularjs
          const result = await tabularjs(blob);

          // Clear previous instance if exists
          if (jssInstance.current && spreadsheetRef.current) {
            jspreadsheet.destroy(spreadsheetRef.current as any);
            jssInstance.current = null;
          }

          // Create jspreadsheet instance
          if (spreadsheetRef.current) {
            jssInstance.current = jspreadsheet(
              spreadsheetRef.current as any,
              result as any
            );
          }
        } else if (fileType === "wordProcessor") {
          // For word processor files, show download message
          setError(
            "Word processor files cannot be previewed. Click the download link to view the file."
          );
        }
      } catch (err) {
        console.error("Error loading file:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load file preview"
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadFile();

    // Cleanup function
    return () => {
      if (jssInstance.current && spreadsheetRef.current) {
        try {
          jspreadsheet.destroy(spreadsheetRef.current as any);
          jssInstance.current = null;
        } catch (err) {
          console.error("Error destroying jspreadsheet:", err);
        }
      }
    };
  }, [isOpen, fileUrl, fileType]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-[90vw] max-w-[1200px] max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-burntBlack">File Preview</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {isLoading && (
            <div className="flex items-center justify-center h-full min-h-[400px]">
              <Spinner />
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center h-full min-h-[400px]">
              <div className="text-center">
                <p className="text-red-500 mb-4">{error}</p>
                {fileType === "wordProcessor" && (
                  <a
                    href={fileUrl}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary/80 underline"
                  >
                    Download File
                  </a>
                )}
              </div>
            </div>
          )}

          {!isLoading && !error && (
            <div
              ref={spreadsheetRef}
              className="w-full overflow-auto"
              style={{ minHeight: "400px" }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default FilePreviewModal;
